import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil as EditIcon, Trash2 as DeleteIcon, Play as ExecuteIcon } from 'lucide-react';
import { toolService } from '../services/toolService';
import { useNotificationStore } from '../store/notificationStore';
import { CodeEditor } from '../components/common/CodeEditor';
import { ToolExecutionDialog } from '../components/tools/ToolExecutionDialog';
import { ToolExecutionHistory } from '../components/tools/ToolExecutionHistory';
import { ToolExample } from '../components/tools/ToolExample';
import { FormSection } from '@coordio/ui/layout';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import { useConfirmDialog } from '../components/common/ConfirmDialog';
import { executionLocationDisplayLabel, type ToolDetailDto, type ToolRiskLevel } from '../types/tool';

const riskLabel = (risk: ToolRiskLevel) => {
  switch (risk) {
    case 0:
      return 'Low';
    case 1:
      return 'Medium';
    case 2:
      return 'High';
    case 3:
      return 'Critical';
    default:
      return 'Unknown';
  }
};

/**
 * Tool detail page component.
 */
export const ToolDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  const [tool, setTool] = useState<ToolDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executeOpen, setExecuteOpen] = useState(false);

  const loadTool = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await toolService.getTool(id);
      setTool(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load tool';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    if (!tool) return;
    if (tool.isSystem) {
      addNotification('System tools cannot be deleted', 'warning');
      return;
    }
    const ok = await confirm({
      title: 'Delete tool',
      message: `Are you sure you want to delete "${tool.displayName}"?`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await toolService.deleteTool(tool.id);
      addNotification('Tool deleted successfully', 'success');
      navigate('/tools');
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete tool', 'error');
    }
  };

  return (
    <>
      <PageDetailLayout
        data-testid="tool-form-page"
        title={tool?.displayName || 'Tool'}
        subtitle={tool?.name}
        breadcrumbs={[{ label: 'Tools', href: '/tools' }, { label: tool?.displayName || 'Tool' }]}
        backTo={{ label: 'Tools', href: '/tools' }}
        loading={loading}
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        status={tool ? { label: tool.isActive ? 'Active' : 'Inactive', color: tool.isActive ? 'success' : 'default' } : undefined}
        chips={tool ? [
          { label: tool.category, variant: 'outlined' as const },
          { label: tool.isDefault ? 'Default' : 'Custom', variant: 'outlined' as const },
          { label: `Runs on: ${executionLocationDisplayLabel(tool.executionLocation)}`, variant: 'outlined' as const },
          { label: `Risk: ${riskLabel(tool.riskLevel)}`, color: 'warning' as const, variant: 'outlined' as const },
          ...(tool.requiresApproval ? [{ label: 'Requires approval', color: 'warning' as const }] : []),
        ] : []}
        actions={tool ? [
          { icon: <ExecuteIcon />, label: 'Execute', onClick: () => setExecuteOpen(true), color: 'primary' as const },
          { icon: <EditIcon />, label: 'Edit', onClick: () => navigate(`/tools/${tool.id}/edit`) },
          { icon: <DeleteIcon />, label: 'Delete', onClick: handleDelete, color: 'error' as const },
        ] : []}
        tabs={tool ? [
          {
            label: 'Overview',
            value: 'overview',
            content: (
              <FormSection data-testid="tool-section-overview" title="Overview">
                <p className="text-sm mb-2">
                  {tool.description || 'No description provided.'}
                </p>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Version: {tool.version}
                </p>
              </FormSection>
            ),
          },
          {
            label: 'Configuration',
            value: 'configuration',
            content: (
              <div className="flex flex-col gap-6">
                <FormSection data-testid="tool-section-implementationType" title="Implementation Type">
                  <p className="text-sm">{tool.implementationType || 'CSharp'}</p>
                </FormSection>

                {tool.documentationUrl && (
                  <FormSection data-testid="tool-section-documentation" title="Documentation">
                    <p className="text-sm">{tool.documentationUrl}</p>
                  </FormSection>
                )}

                <FormSection data-testid="tool-section-implementationDetails" title="Implementation Details (JSON)">
                  <CodeEditor value={tool.implementationJson || '{}'} language="json" height="220px" readOnly />
                </FormSection>
              </div>
            ),
          },
          {
            label: 'Schemas',
            value: 'schemas',
            content: (
              <div className="flex flex-col gap-6">
                <FormSection data-testid="tool-section-inputSchema" title="Input Schema">
                  <CodeEditor value={tool.inputSchemaJson || '{}'} language="json" height="220px" readOnly />
                </FormSection>
                <FormSection data-testid="tool-section-outputSchema" title="Output Schema">
                  <CodeEditor value={tool.outputSchemaJson || '{}'} language="json" height="220px" readOnly />
                </FormSection>
              </div>
            ),
          },
          {
            label: 'Examples',
            value: 'examples',
            content: (
              <ToolExample toolName={tool.name} isDefault={tool.isDefault} exampleUsageJson={tool.exampleUsageJson} />
            ),
          },
          {
            label: 'Execution History',
            value: 'execution-history',
            content: <ToolExecutionHistory toolId={tool.id} />,
          },
        ] : []}
      />
      {tool && <ToolExecutionDialog open={executeOpen} onClose={() => setExecuteOpen(false)} toolName={tool.name} />}
    </>
  );
};
